// useTableFieldActions.js

import { useCallback, useState } from 'react';

import { generateKey } from 'utilities/Keys';
import { createSchemaField } from 'utilities/NodeSchemaFields';
import { FIELD_LOG_MESSAGES, FIELD_MESSAGES } from 'node/form/constants/fieldMessages';

import { updateView } from 'services/views/ViewWriteService';
import { addNodeTypeField } from 'services/nodetypes/NodeTypeMutationService';
import { syncFormsWithTypeSchema } from 'services/forms/FormSyncService';
import {
    warmNodeTypesCacheForBase,
} from 'services/nodetypes/NodeTypesCacheService';
import { canPersistViewLayout } from 'views/utils/organicViews';

import { db } from 'firebaseConfig';

const useTableFieldActions = ({
    view,
    typeDef,
    nodes,
    visibleFieldKeys,
    hasExplicitProperties,
}) => {
    const [fieldModalOpen, setFieldModalOpen] = useState(false);
    const [fieldSaving, setFieldSaving] = useState(false);

    const resolveNodeKeyForType = useCallback((typeKey) => {
        if (!typeKey) return view?.nodeKey || null;
        const matchingNode = Array.isArray(nodes)
            ? nodes.find((node) => node?.type === typeKey || node?.typeKey === typeKey)
            : null;
        return matchingNode?.key || view?.nodeKey || nodes?.[0]?.key || null;
    }, [nodes, view?.nodeKey]);

    const handleFieldModalOpen = useCallback((open) => {
        setFieldModalOpen(open);
    }, []);

    const handleAddFieldClick = useCallback(() => {
        const typeKey = typeDef?.key ? String(typeDef.key).trim() : '';
        if (!typeKey) {
            alert(FIELD_MESSAGES.TYPE_DEFINITION_NOT_READY);
            return;
        }
        const nodeKey = resolveNodeKeyForType(typeKey);
        if (!nodeKey) {
            alert(FIELD_MESSAGES.CREATE_ROW_BEFORE_ADD);
            return;
        }
        handleFieldModalOpen(true);
    }, [handleFieldModalOpen, resolveNodeKeyForType, typeDef?.key]);

    const handleFieldSave = useCallback(async (fieldDraft) => {
        if (!fieldDraft?.label || !fieldDraft?.type || fieldSaving) return;

        const typeKey = typeDef?.key ? String(typeDef.key).trim() : '';
        const nodeKey = resolveNodeKeyForType(typeKey);
        if (!nodeKey || !typeKey) {
            alert(FIELD_MESSAGES.NODE_OR_TYPE_NOT_READY);
            return;
        }
        const resolvedNode = Array.isArray(nodes)
            ? nodes.find((node) => node?.key === nodeKey)
            : null;
        const resolvedBaseKey = typeDef?.baseKey
            || resolvedNode?.baseKey
            || null;

        const nextField = createSchemaField({
            name: generateKey(),
            label: fieldDraft.label,
            fieldDescription: fieldDraft.fieldDescription,
            type: fieldDraft.type,
            targetType: fieldDraft.targetType,
            allowMultiple: fieldDraft.allowMultiple,
            checklistItems: fieldDraft.checklistItems,
            allowCustomItems: fieldDraft.allowCustomItems,
            defaultValue: fieldDraft.default,
            defaultUnit: fieldDraft.defaultUnit,
            options: fieldDraft.options,
            enumOptionsSort: fieldDraft.enumOptionsSort,
        });

        setFieldSaving(true);
        try {
            await addNodeTypeField({
                nodeKey,
                typeKey,
                baseKey: resolvedBaseKey,
                field: nextField,
            });

            if (canPersistViewLayout(view) && hasExplicitProperties) {
                const nextProperties = Array.isArray(visibleFieldKeys)
                    ? [...visibleFieldKeys, nextField.name]
                    : [nextField.name];
                await updateView(view.key, {
                    properties: nextProperties,
                });
            }

            await warmNodeTypesCacheForBase(db, {
                baseKey: resolvedBaseKey,
                subscribe: false,
                ttlMs: 0,
            });

            const nextFields = [
                ...(Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : []),
                nextField,
            ];
            await syncFormsWithTypeSchema({
                typeDef: { schema: { fields: nextFields } },
                typeKey,
                baseKey: resolvedBaseKey,
            }).catch((error) => {
                console.error('[useTableFieldActions] form sync failed after add', error);
            });

            handleFieldModalOpen(false);
        } catch (error) {
            console.error(FIELD_LOG_MESSAGES.TABLE_ADD_FIELD_FAILED, error);
            alert(error?.message || FIELD_MESSAGES.ADD_FAILED);
        } finally {
            setFieldSaving(false);
        }
    }, [
        handleFieldModalOpen,
        hasExplicitProperties,
        nodes,
        fieldSaving,
        resolveNodeKeyForType,
        typeDef?.key,
        typeDef?.baseKey,
        typeDef?.schema?.fields,
        view,
        visibleFieldKeys,
    ]);

    return {
        fieldModalOpen,
        fieldSaving,
        handleFieldModalOpen,
        handleAddFieldClick,
        handleFieldSave,
    };
};

export default useTableFieldActions;
